import React, { useState, useEffect, useCallback } from 'react';
import { supportApi, formatApiErrorDetail } from '../../lib/api';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Headset, Plus, Loader2, Eye, CheckCircle, XCircle, Wifi, WifiOff, KeyRound, Copy, Check, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/Pagination';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function SupportManagementPage() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', max_concurrent_chats: 5 });
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({ total: 0, pages: 0 });
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetAgent, setResetAgent] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [resettingPw, setResettingPw] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      const { data } = await supportApi.listAgents({ page, limit: 20 });
      setAgents(data.items || data);
      setPaginationData({ total: data.total || 0, pages: data.pages || 0 });
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [page]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await supportApi.getAnalytics();
      setAnalytics(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchAgents(); fetchAnalytics(); }, [fetchAgents, fetchAnalytics]);

  // Auto-refresh online status every 10s
  useEffect(() => {
    const interval = setInterval(fetchAgents, 10000);
    return () => clearInterval(interval);
  }, [fetchAgents]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await supportApi.createAgent(form);
      toast.success('Support agent created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', max_concurrent_chats: 5 });
      fetchAgents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to create agent');
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (agent) => {
    try {
      const newStatus = agent.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await supportApi.updateAgent(agent.id, { status: newStatus });
      toast.success(`Agent ${newStatus.toLowerCase()}`);
      fetchAgents();
    } catch (e) {
      toast.error('Failed to update agent');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const specials = '@#$!';
    let pw = '';
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    pw += specials[Math.floor(Math.random() * specials.length)];
    return pw;
  };

  const handleResetPassword = async () => {
    const pw = generatePassword();
    setResettingPw(true);
    setCopied(false);
    try {
      await supportApi.resetPassword(resetAgent.id, { password: pw });
      setGeneratedPassword(pw);
      await navigator.clipboard.writeText(pw);
      setCopied(true);
      toast.success('Password reset and copied to clipboard');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to reset password');
    } finally {
      setResettingPw(false);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="support-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Support Team</h1>
            <p className="text-slate-600 mt-1">Manage support agents and view chat analytics</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#005DE8] hover:bg-[#0047B3]" data-testid="create-agent-button">
            <Plus className="h-4 w-4 mr-2" /> Add Agent
          </Button>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-blue-600">{analytics.total_chats}</p>
                <p className="text-sm text-slate-500">Total Chats</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-red-600">{analytics.unassigned}</p>
                <p className="text-sm text-slate-500">Unassigned</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-emerald-600">{analytics.avg_rating || '-'}</p>
                <p className="text-sm text-slate-500">Avg Rating ({analytics.rated_count} rated)</p>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-2xl font-bold text-slate-600">{analytics.closed}</p>
                <p className="text-sm text-slate-500">Resolved</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Agents Table */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Support Agents</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : agents.length === 0 ? (
              <div className="text-center py-12">
                <Headset className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No support agents</h3>
                <p className="text-slate-500 mt-1">Create your first support agent to handle customer chats</p>
              </div>
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Online</TableHead>
                    <TableHead className="text-center">Active Chats</TableHead>
                    <TableHead className="text-center">Max Chats</TableHead>
                    <TableHead className="text-center">Total Handled</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agents.map(agent => (
                    <TableRow key={agent.id}>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell className="text-slate-500">{agent.email}</TableCell>
                      <TableCell>
                        <Badge className={agent.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border' : 'bg-red-100 text-red-700 border'}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {agent.is_online ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm"><Wifi className="h-4 w-4" /> Online</span>
                        ) : (
                          <span className="flex items-center gap-1 text-slate-400 text-sm"><WifiOff className="h-4 w-4" /> Offline</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center font-semibold">{agent.profile?.active_chat_count || 0}</TableCell>
                      <TableCell className="text-center">{agent.profile?.max_concurrent_chats || 5}</TableCell>
                      <TableCell className="text-center">{agent.profile?.total_chats_handled || 0}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`agent-actions-${agent.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setResetAgent(agent); setGeneratedPassword(''); setCopied(false); setShowResetPw(true); }}>
                              <KeyRound className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            {agent.status === 'ACTIVE' && (
                              <DropdownMenuItem onClick={() => toggleStatus(agent)} className="text-red-600">
                                <XCircle className="h-4 w-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            )}
                            {agent.status !== 'ACTIVE' && (
                              <DropdownMenuItem onClick={() => toggleStatus(agent)} className="text-emerald-600">
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={page} pages={paginationData.pages} total={paginationData.total} onPageChange={setPage} />
              </>
            )}
          </CardContent>
        </Card>

        {/* Chat Volume by Tenant */}
        {analytics?.chats_per_tenant?.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Chat Volume by Tenant</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analytics.chats_per_tenant.map((t, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium">{t.tenant_name}</span>
                    <Badge className="bg-blue-100 text-blue-700">{t.count} chats</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Agent Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Add Support Agent</DialogTitle>
              <DialogDescription>Create a new support agent account</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="agent-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="agent-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required data-testid="agent-password-input" />
                </div>
                <div className="space-y-2">
                  <Label>Max Concurrent Chats</Label>
                  <Input type="number" min="1" max="20" value={form.max_concurrent_chats} onChange={e => setForm({ ...form, max_concurrent_chats: parseInt(e.target.value) || 5 })} data-testid="agent-max-chats-input" />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#005DE8]" disabled={creating} data-testid="create-agent-submit">
                  {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Create Agent
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Reset Password</DialogTitle>
              <DialogDescription>Generate a new password for {resetAgent?.name} ({resetAgent?.email})</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {!generatedPassword ? (
                <p className="text-sm text-slate-500">Click the button below to generate a new random password. It will be automatically copied to your clipboard.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border">
                    <code className="flex-1 text-sm font-mono font-bold tracking-wider text-slate-800">{generatedPassword}</code>
                    <Button variant="ghost" size="sm" onClick={copyToClipboard} className="h-8 w-8 p-0 shrink-0" data-testid="copy-pw-button">
                      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-slate-400" />}
                    </Button>
                  </div>
                  <p className="text-xs text-emerald-600 font-medium">{copied ? 'Copied to clipboard!' : 'Click the copy icon to copy again'}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetPw(false)}>Close</Button>
              <Button className="bg-[#005DE8]" onClick={handleResetPassword} disabled={resettingPw} data-testid="reset-pw-submit">
                {resettingPw ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                {generatedPassword ? 'Generate New' : 'Generate & Copy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
